import { Box, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyboardEvent, MouseEvent, useEffect, useState } from 'react';

// Types
import { ApiError } from '../../@types';
import { SuperAdminFlags, SuperAdminProviderStatuses } from '../../@types/api';

// Constants
import { QUERY_KEYS } from '../../constants';
import API_ROUTES from '../../constants/apiRoutes';

// Hooks
import { useModal } from '../../contexts/ModalContext';
import { useToastErrorMessage } from '../../hooks/useToastErrorMessage';

// Utils
import { getData, patchData } from '../../utils/api';
import { mergeSx } from '../../utils/styles';

// Components
import NumberInput from '../atoms/NumberInput';
import Switch from '../atoms/Switch';
import ConfirmationModal from '../modals/ConfirmationModal';

type NumericField = 'ftbResolveBufferMaxDelayedJobs' | 'ftbResolveBufferDelayS';
type BooleanField = Exclude<keyof SuperAdminFlags, NumericField>;

type SettingDefinition<Field extends keyof SuperAdminFlags> = {
  field: Field;
  label: string;
  description: string;
};

type ProviderStatusDefinition = {
  field: keyof SuperAdminProviderStatuses;
  label: string;
};

const BOOLEAN_SETTINGS: SettingDefinition<BooleanField>[] = [
  {
    field: 'feedRunning',
    label: 'Feed Running (Maintenance Mode)',
    description: 'Turn the main feed on or off. When this is off, the client app goes into maintenance mode.',
  },
  {
    field: 'casinoRunning',
    label: 'Casino Running (Maintenance Mode)',
    description: 'Turn the casino on or off. When this is off, the client app shows the casino maintenance screen.',
  },
  {
    field: 'manualTemplateSwitch',
    label: 'Manual Template Switch',
    description:
      'Allow admins to switch templates manually. When this is off, template selection stays in automatic mode.',
  },
  {
    field: 'includeNuMessages',
    label: 'Include NU Messages',
    description:
      'Include NU messages from Feed The Bet instead of skipping them. Leave this off to keep the current default behavior.',
  },
  {
    field: 'ftbTicketValidation',
    label: 'FTB Ticket Validation',
    description: 'Enable ticket validation on the Feed The Bet side before tickets are processed.',
  },
  {
    field: 'ftbSettlementResolveBuffer',
    label: 'FTB Settlement Resolve Buffer',
    description: 'Delay settlement resolving through a buffer instead of processing settlements immediately.',
  },
  {
    field: 'feedKillSwitch',
    label: 'Feed Kill Switch',
    description: 'Immediately stop feed-driven updates across the platform.',
  },
  {
    field: 'deactivateOtherOutcomes',
    label: 'Deactivate Other Outcomes',
    description: 'Deactivate only FTB outcomes missing from the latest fixture update.',
  },
];

const NUMERIC_SETTINGS: SettingDefinition<NumericField>[] = [
  {
    field: 'ftbResolveBufferMaxDelayedJobs',
    label: 'FTB Resolve Buffer Max Delayed Jobs',
    description: 'Maximum delayed jobs allowed in the FTB resolve buffer queue.',
  },
  {
    field: 'ftbResolveBufferDelayS',
    label: 'FTB Resolve Buffer Delay (s)',
    description: 'Delay in seconds applied to FTB resolve buffer jobs.',
  },
];

const PROVIDER_STATUS_SETTINGS: ProviderStatusDefinition[] = [
  {
    field: 'providerRunningSM',
    label: 'SM',
  },
  {
    field: 'providerRunningVB',
    label: 'VB',
  },
  {
    field: 'providerRunningB3',
    label: 'B3',
  },
  {
    field: 'providerRunningXB',
    label: 'XB',
  },
];

const styles = {
  page: {
    width: '100%',
    maxWidth: 1440,
  },
  contentGrid: {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 3fr) minmax(0, 2fr)' },
    gap: 3,
  },
  leftColumn: {
    minWidth: 0,
  },
  rightColumn: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: { xs: 'stretch', xl: 'flex-end' },
  },
  section: {
    p: { xs: 2, md: 3 },
    borderRadius: 2,
  },
  row: {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 280px' },
    py: 2,
    gap: { xs: 2, md: 3 },
    alignItems: 'center',
    '&:not(:last-of-type)': {
      borderBottom: '1px solid',
      borderColor: 'divider',
    },
  },
  rowText: {
    minWidth: 0,
    pr: { md: 2 },
  },
  fieldTitle: {
    fontWeight: 600,
  },
  control: {
    width: '100%',
    minWidth: 0,
    alignItems: { xs: 'flex-start', md: 'stretch' },
  },
  controlRow: {
    width: '100%',
    justifyContent: { xs: 'space-between', md: 'flex-end' },
  },
  statusSlot: {
    width: 18,
    height: 18,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  helperText: {
    mt: 0.5,
  },
  providerCard: {
    width: { xs: '100%', xl: 'fit-content' },
    maxWidth: '100%',
    minWidth: { xs: '100%', xl: 320 },
    p: { xs: 2, md: 3 },
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'divider',
  },
  providerCardTitle: {
    fontWeight: 700,
  },
  providerStatusGrid: {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
    gap: 1.5,
  },
  providerStatusItem: {
    p: 1.5,
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.default',
  },
  providerStatusItemHeader: {
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 1,
  },
  providerStatusDot: {
    width: 14,
    height: 14,
    borderRadius: '50%',
    flexShrink: 0,
    border: '2px solid',
    borderColor: 'common.white',
    boxShadow: '0 0 0 1px rgba(15, 23, 42, 0.14)',
  },
  spinnerWrapper: {
    minHeight: '40vh',
    alignItems: 'center',
    justifyContent: 'center',
  },
};

const getDirtyHelperText = (isDirty: boolean, isSaving: boolean) => {
  if (isSaving) {
    return 'Saving...';
  }

  if (isDirty) {
    return 'Press Enter to save';
  }

  return ' ';
};

const SuperAdminFlagsPage = () => {
  const queryClient = useQueryClient();
  const { openModal, closeModal } = useModal();
  const { handleErrorMessage } = useToastErrorMessage();

  const { data, isPending, isError, error } = useQuery<SuperAdminFlags, ApiError>({
    queryKey: [QUERY_KEYS.superAdminFlags],
    queryFn: () => getData(API_ROUTES.SUPER_ADMIN_FLAGS, undefined, 'ftbConsumer'),
  });
  const {
    data: providerStatuses,
    isPending: isProviderStatusesLoading,
    isError: isProviderStatusesError,
    error: providerStatusesError,
  } = useQuery<SuperAdminProviderStatuses, ApiError>({
    queryKey: [QUERY_KEYS.superAdminProviderStatuses],
    queryFn: () => getData(API_ROUTES.SUPER_ADMIN_PROVIDER_STATUSES, undefined, 'ftbConsumer'),
  });

  const [draftFlags, setDraftFlags] = useState<SuperAdminFlags | null>(null);
  const [numericDrafts, setNumericDrafts] = useState<Record<NumericField, string>>({
    ftbResolveBufferMaxDelayedJobs: '',
    ftbResolveBufferDelayS: '',
  });
  const [pendingFields, setPendingFields] = useState<Partial<Record<keyof SuperAdminFlags, boolean>>>({});

  useEffect(() => {
    if (data) {
      setDraftFlags(data);
      setNumericDrafts({
        ftbResolveBufferMaxDelayedJobs: String(data.ftbResolveBufferMaxDelayedJobs),
        ftbResolveBufferDelayS: String(data.ftbResolveBufferDelayS),
      });
    }
  }, [data]);

  useEffect(() => {
    if (isError && error) {
      handleErrorMessage(error);
    }
  }, [error, handleErrorMessage, isError]);

  useEffect(() => {
    if (isProviderStatusesError && providerStatusesError) {
      handleErrorMessage(providerStatusesError);
    }
  }, [handleErrorMessage, isProviderStatusesError, providerStatusesError]);

  const updateMutation = useMutation<void, ApiError, Partial<SuperAdminFlags>>({
    mutationFn: async (payload) => {
      await patchData(API_ROUTES.SUPER_ADMIN_FLAGS, payload, 'ftbConsumer');
    },
  });

  const setFieldPending = (field: keyof SuperAdminFlags, isPending: boolean) => {
    setPendingFields((current) => ({
      ...current,
      [field]: isPending,
    }));
  };

  const refetchFlags = async () => {
    await queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.superAdminFlags],
    });
  };

  const revertBooleanField = (field: BooleanField) => {
    if (!data) {
      return;
    }

    setDraftFlags((current) =>
      current
        ? {
            ...current,
            [field]: data[field],
          }
        : current
    );
  };

  const revertNumericField = (field: NumericField) => {
    if (!data) {
      return;
    }

    setNumericDrafts((current) => ({
      ...current,
      [field]: String(data[field]),
    }));
  };

  const saveBooleanField = (field: BooleanField, value: boolean) => {
    setDraftFlags((current) =>
      current
        ? {
            ...current,
            [field]: value,
          }
        : current
    );

    setFieldPending(field, true);

    updateMutation.mutate(
      { [field]: value },
      {
        onSuccess: async () => {
          await refetchFlags();
        },
        onError: (mutationError) => {
          handleErrorMessage(mutationError);
          revertBooleanField(field);
        },
        onSettled: () => {
          setFieldPending(field, false);
        },
      }
    );
  };

  const openBooleanConfirmationModal = (field: BooleanField, label: string, nextValue: boolean) => {
    openModal(
      <ConfirmationModal
        title={`Are you sure you want to ${nextValue ? 'enable' : 'disable'} ${label}?`}
        actionButtonLabel='Confirm'
        handleConfirm={() => saveBooleanField(field, nextValue)}
        closeModal={closeModal}
        actionButtonClose='Cancel'
      />
    );
  };

  const saveNumericField = (field: NumericField) => {
    const draftValue = numericDrafts[field];
    const parsedValue = Number(draftValue);

    if (!data) {
      return;
    }

    if (!draftValue || Number.isNaN(parsedValue)) {
      revertNumericField(field);
      return;
    }

    if (parsedValue === data[field]) {
      return;
    }

    setFieldPending(field, true);

    updateMutation.mutate(
      { [field]: parsedValue },
      {
        onSuccess: async () => {
          await refetchFlags();
        },
        onError: (mutationError) => {
          handleErrorMessage(mutationError);
          revertNumericField(field);
        },
        onSettled: () => {
          setFieldPending(field, false);
        },
      }
    );
  };

  const handleBooleanClick = (field: BooleanField, label: string) => (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!draftFlags || pendingFields[field]) {
      return;
    }

    openBooleanConfirmationModal(field, label, !draftFlags[field]);
  };

  const handleNumericChange = (field: NumericField) => (value: string | number | null) => {
    setNumericDrafts((current) => ({
      ...current,
      [field]: value == null ? '' : String(value),
    }));
  };

  const handleNumericKeyDown = (field: NumericField) => (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      saveNumericField(field);
    }
  };

  const isFlagsLoading = !isError && (isPending || !draftFlags);
  const isProviderStatusesPending = !isProviderStatusesError && (isProviderStatusesLoading || !providerStatuses);

  if (isFlagsLoading || isProviderStatusesPending) {
    return (
      <Stack sx={styles.spinnerWrapper}>
        <CircularProgress />
      </Stack>
    );
  }

  if (!draftFlags) {
    return (
      <Stack spacing={1}>
        <Typography variant='h4'>Super Admin Flags</Typography>
        <Typography color='error'>Failed to load super admin flags.</Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={3} sx={styles.page}>
      <Box sx={styles.contentGrid}>
        <Stack spacing={3} sx={styles.leftColumn}>
          <Paper elevation={0} sx={styles.section}>
            <Stack spacing={0}>
              {BOOLEAN_SETTINGS.map(({ field, label, description }) => (
                <Stack key={field} sx={styles.row}>
                  <Stack sx={styles.rowText}>
                    <Typography variant='h6' sx={styles.fieldTitle}>
                      {label}
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      {description}
                    </Typography>
                  </Stack>

                  <Stack
                    direction='row'
                    sx={mergeSx(
                      {
                        alignItems: 'center',
                        gap: 1,
                      },
                      mergeSx(styles.control, styles.controlRow)
                    )}
                  >
                    <Box sx={styles.statusSlot}>{pendingFields[field] ? <CircularProgress size={18} /> : null}</Box>
                    <Switch
                      value={draftFlags[field]}
                      onClick={handleBooleanClick(field, label)}
                      disabled={Boolean(pendingFields[field])}
                    />
                  </Stack>
                </Stack>
              ))}
            </Stack>
          </Paper>

          <Paper elevation={0} sx={styles.section}>
            <Stack spacing={0}>
              {NUMERIC_SETTINGS.map(({ field, label, description }) => {
                const serverValue = data?.[field];
                const isDirty = numericDrafts[field] !== String(serverValue ?? '');
                const isSaving = Boolean(pendingFields[field]);
                const helperText = getDirtyHelperText(isDirty, isSaving).trim();

                return (
                  <Stack key={field} sx={styles.row}>
                    <Stack sx={styles.rowText}>
                      <Typography variant='h6' sx={styles.fieldTitle}>
                        {label}
                      </Typography>
                      <Typography
                        variant='body2'
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        {description}
                      </Typography>
                    </Stack>

                    <Stack spacing={helperText ? 0.5 : 0} sx={styles.control}>
                      <Stack
                        direction='row'
                        sx={{
                          alignItems: 'center',
                          gap: 1,
                          width: '100%',
                        }}
                      >
                        <Box sx={styles.statusSlot}>{isSaving ? <CircularProgress size={18} /> : null}</Box>
                        <NumberInput
                          value={numericDrafts[field]}
                          onChange={handleNumericChange(field)}
                          onKeyDown={handleNumericKeyDown(field)}
                          disabled={isSaving}
                          size='small'
                          fullWidth
                          valueAsString
                        />
                      </Stack>
                      {helperText ? (
                        <Typography
                          variant='caption'
                          sx={mergeSx(
                            {
                              color: 'text.secondary',
                            },
                            styles.helperText
                          )}
                        >
                          {helperText}
                        </Typography>
                      ) : null}
                    </Stack>
                  </Stack>
                );
              })}
            </Stack>
          </Paper>
        </Stack>

        <Box sx={styles.rightColumn}>
          <Paper elevation={0} sx={styles.providerCard}>
            <Stack spacing={2}>
              <Typography variant='h6' sx={styles.providerCardTitle}>
                Provider Running Flags
              </Typography>

              <Box sx={styles.providerStatusGrid}>
                {PROVIDER_STATUS_SETTINGS.map(({ field, label }) => {
                  const isLoadingStatus = isProviderStatusesLoading && !providerStatuses;
                  const statusValue = providerStatuses?.[field];
                  const statusLabel = providerStatuses == null ? 'Unavailable' : statusValue ? 'Active' : 'Inactive';

                  return (
                    <Stack key={field} sx={styles.providerStatusItem}>
                      <Stack direction='row' sx={styles.providerStatusItemHeader}>
                        <Typography variant='subtitle1' sx={styles.fieldTitle}>
                          {label}
                        </Typography>
                        {isLoadingStatus ? (
                          <CircularProgress size={18} />
                        ) : (
                          <Box
                            role='status'
                            aria-label={`${label} ${statusLabel}`}
                            sx={mergeSx(styles.providerStatusDot, {
                              backgroundColor:
                                providerStatuses == null
                                  ? 'text.disabled'
                                  : statusValue
                                    ? 'success.main'
                                    : 'error.main',
                            })}
                          />
                        )}
                      </Stack>
                    </Stack>
                  );
                })}
              </Box>
            </Stack>
          </Paper>
        </Box>
      </Box>
    </Stack>
  );
};

export default SuperAdminFlagsPage;
